import { atom } from "recoil"

import { Plugin } from "./plugins"
import persistAtom from "./recoilPersist"

/**
 * Event plugin state structure
 * Used for rendering enabled plugins on an event page
 */
export interface EventPluginState {
  plugins: Record<string, Plugin> // Map of plugin ID to plugin data
  configurations: Record<string, any> // Map of plugin ID to configuration
  isLoading: boolean
  hasError: boolean // Keep for backward compatibility, but will be deprecated
  errorMessage?: string // Keep for backward compatibility, but will be deprecated
  pluginErrors: Record<string, string> // Map of plugin ID to error message
}

/**
 * Root atom for event plugins
 * Stores plugins by event ID
 */
export interface EventPluginsState {
  events: Record<string, EventPluginState> // Map of event ID to event plugin state
  lastUpdated: Record<string, number> // Map of event ID to last updated timestamp
}

/**
 * Event plugins atom
 * Used to store and manage plugins that are enabled on events
 */
export const eventPluginsAtom = atom<EventPluginsState>({
  key: "eventPluginsAtom",
  default: {
    events: {},
    lastUpdated: {},
  },
  effects_UNSTABLE: [persistAtom],
})

/**
 * Helper function to get all plugins for an event
 * @param state - The event plugins state
 * @param eventId - The event ID to get plugins for
 * @returns Array of plugins for the event
 */
export function getEventPlugins(
  state: EventPluginsState,
  eventId: string,
): Plugin[] {
  const eventState = state.events[eventId]
  if (!eventState) return []

  return Object.values(eventState.plugins)
}

/**
 * Helper function to get a specific plugin for an event
 * @param state - The event plugins state
 * @param eventId - The event ID
 * @param pluginId - The plugin ID
 * @returns The plugin or null if not found
 */
export function getEventPlugin(
  state: EventPluginsState,
  eventId: string,
  pluginId: string,
): Plugin | null {
  const eventState = state.events[eventId]
  if (!eventState) return null

  return eventState.plugins[pluginId] || null
}

/**
 * Helper function to get the configuration for a plugin
 * @param state - The event plugins state
 * @param eventId - The event ID
 * @param pluginId - The plugin ID
 * @returns The plugin configuration or null if not found
 */
export function getPluginConfiguration(
  state: EventPluginsState,
  eventId: string,
  pluginId: string,
): any {
  const eventState = state.events[eventId]
  if (!eventState) return null

  return eventState.configurations[pluginId] || null
}
